import {
  filterConditionSchema,
  smartListSortSchema,
  SMART_LIST_VISIBILITIES,
  type FilterCondition,
  type FilterDefinition,
  type LeadCatalogResponse,
  type SmartListChange,
  type SmartListSort,
  type SmartListVisibility,
} from "@gioia365/contracts";
import { diffDefinitions, diffSort } from "@gioia365/domain/smart-list-diff";
import type { Locale } from "@gioia365/i18n";
import { z } from "zod";
import type { Translate } from "../i18n/locale";
import { sortDescription, type ColumnDef } from "../leads/columns/table-layout";
import { conditionText, fieldLabel, fieldOf, type FilterField } from "../leads/filters/filter-text";

// Le parole della pagina di una Smart List (piano M3 Task 9; mock-up `visibilityText`, `sortLabel`, `namesText` e
// `describeChanges`): chi la vede, l'ordine di chiamata, le differenze in parole semplici (lo «Storico della lista»,
// «Cosa cambia per chi la usa», l'avviso di «Condividi») e le differenze fra la bozza e la lista salvata. Funzioni pure;
// le parole vengono dai cataloghi, in italiano e in inglese.

/** «Chi la vede»: «Privata», «Condivisa con colleghi scelti», «Pubblica al team» (le parole di `smartLists.visibility`). */
export function visibilityText(t: Translate, visibility: SmartListVisibility): string {
  return t(`smartLists.visibility.${visibility}`);
}

/** L'ordine di chiamata in parole: «data di creazione, dal più recente». */
export function sortText(t: Translate, columns: readonly ColumnDef[], sort: SmartListSort): string {
  const words = sortDescription(t, columns, sort);
  return t("smartLists.page.sortText", { column: words.column, direction: words.direction });
}

/** Dei nomi in una frase: «Sara Conti, Luca Neri e Paolo Greco». */
export function namesText(locale: Locale, names: readonly string[]): string {
  return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(names);
}

/** Quello che serve per dire una differenza: i campi dei filtri, i nomi delle voci, le colonne e la lingua. */
export type ChangeFields = {
  /** I campi del pannello «Filtri» (`filterFields`). */
  filterFields: readonly FilterField[];
  /** I campi dell'azienda, per il nome di un campo che il pannello non ha più. */
  catalog: LeadCatalogResponse | undefined;
  /** Il catalogo delle colonne, per l'ordine di chiamata. */
  columns: readonly ColumnDef[];
  /** I nomi delle voci di un campo (status, owner, città…). */
  labelsOf: (ref: string) => ReadonlyMap<string, string>;
  /** Un giorno `AAAA-MM-GG` in numeri. */
  formatDay: (day: string) => string;
  locale: Locale;
};

const personSchema = z.object({ membershipId: z.string(), displayName: z.string() });

/** Le condizioni di un valore di una differenza: una condizione, o più d'una per lo stesso campo. */
export function conditionsOfValue(value: unknown): FilterCondition[] {
  const entries = Array.isArray(value) ? value : [value];
  return entries.flatMap((entry) => {
    const parsed = filterConditionSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

/** Le condizioni dei filtri nominate in un elenco di differenze, per leggerne in anticipo i nomi delle voci. */
export function conditionsInChanges(changes: readonly SmartListChange[]): FilterCondition[] {
  return changes.flatMap((change) =>
    change.kind === "filter_added" || change.kind === "filter_changed" || change.kind === "filter_removed"
      ? [...conditionsOfValue(change.before), ...conditionsOfValue(change.after)]
      : [],
  );
}

/**
 * Ogni differenza in una riga, come nel mock-up: «Aggiunto il filtro Status: In target.», «Status: da «In target» a «In
 * target, Qualificato».», «Ordine di chiamata: da «…» a «…».», «Chi la vede: Pubblica al team.», «Creata.». I destinatari
 * tolti da un cambio di «Chi la vede» non aggiungono una riga (la dice già «Chi la vede»).
 */
export function describeChanges(t: Translate, changes: readonly SmartListChange[], fields: ChangeFields): string[] {
  const valueText = (value: unknown): string => {
    const texts = conditionsOfValue(value).map((condition) => {
      const field = fieldOf(t, fields.filterFields, condition, fields.catalog);
      return conditionText(t, field, condition, fields.labelsOf(field.ref), fields.formatDay);
    });
    return texts.length > 0 ? texts.join(", ") : "—";
  };
  const fieldName = (ref: string): string => fields.filterFields.find((field) => field.ref === ref)?.label ?? fieldLabel(t, ref, fields.catalog);
  const sortOf = (value: unknown): string => {
    const parsed = smartListSortSchema.safeParse(value);
    return parsed.success ? sortText(t, fields.columns, parsed.data) : "—";
  };
  const personName = (value: unknown): string => {
    const parsed = personSchema.safeParse(value);
    return parsed.success ? parsed.data.displayName : "—";
  };
  const text = (value: unknown): string => (typeof value === "string" ? value : "—");

  return changes.flatMap((change): string[] => {
    switch (change.kind) {
      case "filter_added":
        if (change.field === undefined) return [t("smartLists.page.changes.structure")];
        return [t("smartLists.page.changes.filterAdded", { field: fieldName(change.field), value: valueText(change.after) })];
      case "filter_changed":
        if (change.field === undefined) return [t("smartLists.page.changes.structure")];
        return [t("smartLists.page.changes.filterChanged", { field: fieldName(change.field), before: valueText(change.before), after: valueText(change.after) })];
      case "filter_removed":
        if (change.field === undefined) return [t("smartLists.page.changes.structure")];
        return [t("smartLists.page.changes.filterRemoved", { field: fieldName(change.field), value: valueText(change.before) })];
      case "sort_changed":
        return [t("smartLists.page.changes.sort", { before: sortOf(change.before), after: sortOf(change.after) })];
      case "name_changed":
        return [t("smartLists.page.changes.name", { before: text(change.before), after: text(change.after) })];
      case "visibility_changed": {
        const after = SMART_LIST_VISIBILITIES.find((visibility) => visibility === change.after);
        return [t("smartLists.page.changes.visibility", { after: after ? visibilityText(t, after) : "—" })];
      }
      case "recipients_changed": {
        const people = z.array(personSchema).safeParse(change.after);
        const names = people.success ? people.data.map((person) => person.displayName) : [];
        return names.length > 0 ? [t("smartLists.page.changes.recipients", { names: namesText(fields.locale, names) })] : [];
      }
      case "owner_changed":
        return [t("smartLists.page.changes.owner", { before: personName(change.before), after: personName(change.after) })];
      case "created":
        return [t("smartLists.page.changes.created")];
    }
  });
}

export type ListDraft = { definition: FilterDefinition; sort: SmartListSort };

/**
 * Le differenze fra la lista salvata e la bozza, con la regola dello storico del server (`@gioia365/domain/smart-list-diff`,
 * decisione P30): i filtri per campo e per contenuto (lo stesso filtro in un altro ordine, o con le voci in un altro
 * ordine, non è una modifica), poi l'ordine di chiamata. Vuote: la bozza è la lista salvata («Salvata»); altrimenti
 * «Modifiche non salvate», e sono le righe di «Cosa cambia per chi la usa».
 */
export function draftChanges(saved: ListDraft, draft: ListDraft): SmartListChange[] {
  return [...diffDefinitions(saved.definition, draft.definition), ...diffSort(saved.sort, draft.sort)];
}
